import apiClient from './meta-client';
import type {
    FunnelResponse,
    FunnelDetailResponse,
    FunnelGroupCreateResponse,
    FunnelPageInfo,
    CreateFunnelRequest,
    UpdateFunnelRequest,
    FunnelValidationResponse,
    FunnelExportData,
    FunnelImportRequest,
    FunnelCategory,
    FunnelStatsDetails,
    FunnelStatsExecutions,
    FunnelNodeStats,
} from '@/types/funnel';

// ========== FUNNELS API ==========

// Listar todos os funis do usuário
export const getFunnels = async (
    is_active?: boolean,
    page_id?: string,
    category_id?: number,
    user_id?: number
): Promise<FunnelResponse[]> => {
    const params: Record<string, string> = {};

    // Só adiciona o parâmetro se for realmente um boolean
    if (typeof is_active === 'boolean') {
        params.is_active = String(is_active);
    }

    if (page_id) {
        params.page_id = page_id;
    }

    if (typeof category_id === 'number') {
        params.category_id = String(category_id);
    }

    if (typeof user_id === 'number') {
        params.user_id = String(user_id);
    }

    const queryString = new URLSearchParams(params).toString();

    const response = await apiClient.get<FunnelResponse[]>(
        `/funnels${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
};

// ========== FUNNEL CATEGORIES API ==========

export const getFunnelCategories = async (): Promise<FunnelCategory[]> => {
    const response = await apiClient.get('/meta/funnel-categories');
    return response.data;
};

export const createFunnelCategory = async (data: { name: string }): Promise<FunnelCategory> => {
    const response = await apiClient.post('/meta/funnel-categories', data);
    return response.data;
};

export const updateFunnelCategory = async (id: number, data: { name: string }): Promise<FunnelCategory> => {
    const response = await apiClient.patch(`/meta/funnel-categories/${id}`, data);
    return response.data;
};

export const deleteFunnelCategory = async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/funnel-categories/${id}`);
    return response.data;
};

// Obter funil específico (básico)
export const getFunnel = async (funnelId: number): Promise<FunnelResponse> => {
    const response = await apiClient.get<FunnelResponse>(`/funnels/${funnelId}`);
    return response.data;
};

// Obter detalhes completos do funil (com nodes e transições)
export const getFunnelDetails = async (funnelId: number): Promise<FunnelDetailResponse> => {
    const response = await apiClient.get<FunnelDetailResponse>(`/funnels/${funnelId}/details`);
    return response.data;
};

// Criar novo funil (retorna { funnel: FunnelResponse })
export const createFunnel = async (data: CreateFunnelRequest): Promise<FunnelGroupCreateResponse> => {
    const response = await apiClient.post<FunnelGroupCreateResponse>('/funnels', data);
    return response.data;
};

// Atualizar funil
export const updateFunnel = async (
    funnelId: number,
    data: UpdateFunnelRequest
): Promise<FunnelResponse> => {
    const response = await apiClient.put<FunnelResponse>(`/funnels/${funnelId}`, data);
    return response.data;
};

// Validar funil
export const validateFunnel = async (funnelId: number): Promise<FunnelValidationResponse> => {
    const response = await apiClient.get<FunnelValidationResponse>(`/funnels/${funnelId}/validate`);
    return response.data;
};

// Desempenho do funil no período (datas YYYY-MM-DD)
export const getFunnelStatsDetails = async (funnelId: number, start: string, end: string, pageId?: string | null): Promise<FunnelStatsDetails> => {
    const response = await apiClient.get<FunnelStatsDetails>(`/funnels/${funnelId}/stats/details`, {
        params: { start, end, ...(pageId ? { page_id: pageId } : {}) },
    });
    return response.data;
};

export const getFunnelStatsExecutions = async (
    funnelId: number, start: string, end: string, page = 1, perPage = 500,
): Promise<FunnelStatsExecutions> => {
    const response = await apiClient.get<FunnelStatsExecutions>(`/funnels/${funnelId}/stats/executions`, {
        params: { start, end, page, per_page: perPage },
    });
    return response.data;
};

export const getFunnelNodeStats = async (
    funnelId: number, nodeId: number, start: string, end: string, pageId?: string | null,
): Promise<FunnelNodeStats> => {
    const response = await apiClient.get<FunnelNodeStats>(`/funnels/${funnelId}/stats/nodes/${nodeId}`, {
        params: { start, end, ...(pageId ? { page_id: pageId } : {}) },
    });
    return response.data;
};

// Ativar funil
export const activateFunnel = async (funnelId: number): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.post<{ message: string; success: boolean }>(`/funnels/${funnelId}/activate`);
    return response.data;
};

// Desativar funil
export const deactivateFunnel = async (funnelId: number): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.post<{ message: string; success: boolean }>(`/funnels/${funnelId}/deactivate`);
    return response.data;
};

// Duplicar funil
export const duplicateFunnel = async (funnelId: number): Promise<FunnelResponse> => {
    const response = await apiClient.post<FunnelResponse>(`/funnels/${funnelId}/duplicate`);
    return response.data;
};

// Deletar funil
export const deleteFunnel = async (funnelId: number): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.delete<{ message: string; success: boolean }>(`/funnels/${funnelId}`);
    return response.data;
};

// Adicionar página ao funil
export const addPageToFunnel = async (
    funnelId: number,
    internal_page_id: number
): Promise<FunnelPageInfo> => {
    const response = await apiClient.post<FunnelPageInfo>(`/funnels/${funnelId}/pages`, { internal_page_id });
    return response.data;
};

export const addPagesBatchToFunnel = async (
    funnelId: number,
    internal_page_ids: number[]
): Promise<{ added: number; skipped: number; errors: { page_id: number; error: string }[]; success: boolean }> => {
    const response = await apiClient.post(`/funnels/${funnelId}/pages/batch`, { internal_page_ids });
    return response.data;
};

// Remover página do funil
export const removePageFromFunnel = async (
    funnelId: number,
    funnelPageId: number
): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.delete(`/funnels/${funnelId}/pages/${funnelPageId}`);
    return response.data;
};

// Remover múltiplas páginas do funil em uma única requisição
// Aceita IDs de funnel_pages (vínculo direto) ou internal_page_ids (id em meta_pages)
export const removePagesBatchFromFunnel = async (
    funnelId: number,
    payload: { funnel_page_ids?: number[]; internal_page_ids?: number[] }
): Promise<{ removed: number; skipped: number; errors: any[]; success: boolean }> => {
    const response = await apiClient.delete(`/funnels/${funnelId}/pages/batch`, { data: payload });
    return response.data;
};

// Exportar JSON do funil (nodes, buttons, triggers, transitions)
export const exportFunnel = async (funnelId: number): Promise<FunnelExportData> => {
    const response = await apiClient.get<FunnelExportData>(`/funnels/${funnelId}/export`);
    return response.data;
};

// Importar nodes de JSON em um funil existente
export const importFunnelNodes = async (data: FunnelImportRequest): Promise<FunnelDetailResponse> => {
    const response = await apiClient.post<FunnelDetailResponse>('/funnels/import', data);
    return response.data;
};

// Importar fluxo do ChatFood (cria funnel novo)
export interface ImportChatfoodRequest {
    name: string;
    flow: Record<string, unknown>;
    descricao?: string;
}

export interface ImportChatfoodResponse {
    funnel_id: number;
    trigger_node_id: number;
    nodes_created: number;
    transitions_created: number;
    images_downloaded: number;
    warnings: string[];
}

export const importChatfoodFlow = async (
    data: ImportChatfoodRequest,
): Promise<ImportChatfoodResponse> => {
    const response = await apiClient.post<ImportChatfoodResponse>('/funnels/import-chatfood', data);
    return response.data;
};

// Traduzir funil com IA
export const translateFunnel = async (
    funnelId: number,
    data: { target_language: string; source_language?: string; ai_provider: string }
): Promise<{ success: boolean; task_id: string; message: string }> => {
    const response = await apiClient.post(`/funnels/${funnelId}/translate`, data);
    return response.data;
};

// Consultar status de tradução por task_id
export const getTranslateStatus = async (
    taskId: string
): Promise<{
    task_id: string;
    status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'not_found';
    pages_done: string;
    pages_total: string;
    pages_to_translate?: string;
    pages_skipped?: string;
    nodes_total: string;
    current_page?: string;
    error: string;
}> => {
    const response = await apiClient.get(`/funnels/translate/status/${taskId}`);
    return response.data;
};

// Consultar status de tradução por funnel_id (mais prático para polling)
export const getTranslateStatusByFunnel = async (
    funnelId: number
): Promise<{
    task_id?: string;
    funnel_id: number;
    status: 'idle' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
    pages_done?: string;
    pages_total?: string;
    pages_to_translate?: string;
    pages_skipped?: string;
    nodes_total?: string;
    current_page?: string;
    error?: string;
}> => {
    const response = await apiClient.get(`/funnels/${funnelId}/translate/status`);
    return response.data;
};

// Resumo de tradução dos nodes por página
export const getTranslationSummary = async (
    funnelId: number
): Promise<{
    funnel_id: number;
    total_nodes: number;
    pages: Array<{
        page_id: string;
        page_name?: string;
        language?: string;
        internal_category_name?: string;
        translated_nodes: number;
        total_nodes: number;
        status: 'completed' | 'partial' | 'pending';
    }>;
}> => {
    const response = await apiClient.get(`/funnels/${funnelId}/translate/summary`);
    return response.data;
};

// Consultar status de retradução (validate/retranslate) por task_id
export const getValidateRetranslateStatus = async (
    taskId: string
): Promise<{
    task_id: string;
    status: 'running' | 'completed' | 'failed' | 'not_found';
    pages_done: string;
    pages_total: string;
    current_page?: string;
    error: string;
}> => {
    const response = await apiClient.get(`/funnels/validate-retranslate/status/${taskId}`);
    return response.data;
};

export const validateTranslation = async (
    funnelId: number,
    pageIds?: string[],
    force?: boolean
): Promise<{
    funnel_id: number;
    checked: number;
    ok: number;
    retranslating: number;
    pages_ok: Array<{ page_id: string; page_name?: string; status: 'ok' }>;
    pages_retranslating: Array<{
        page_id: string;
        page_name?: string;
        language?: string;
        translated_nodes: number;
        total_nodes: number;
        status: string;
        task_id?: string;
    }>;
}> => {
    // CSV manual em vez de `params: { page_ids: pageIds }` para não depender
    // da serialização de array do axios (varia entre `page_ids[]=` e `page_ids=`
    // conforme versão/config). O backend aceita ambos os formatos; CSV é o mais previsível.
    const hasPageIds = pageIds && pageIds.length > 0;
    const params: string[] = [];
    if (hasPageIds) {
        params.push(`page_ids=${pageIds.map(encodeURIComponent).join(',')}`);
    }
    // `force` só tem efeito no backend quando page_ids está preenchido.
    if (force && hasPageIds) {
        params.push('force=true');
    }
    const query = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await apiClient.post(`/funnels/${funnelId}/translate/validate${query}`);
    return response.data;
};

// ─── Engine V2 (ver PRD-esteira-multi-template-utility / migração front V2) ───
// V1 acima é mantida intacta e não é removida — só deixa de ser chamada pelos
// componentes migrados (TranslateFunnelModal, ValidateTranslationModal, Toolbar).

// Traduzir funil com IA — engine V2. target_language omitido = cada página
// traduz para o seu próprio idioma atual (meta_pages.language).
export const translateFunnelV2 = async (
    funnelId: number,
    data: { target_language?: string; source_language?: string; ai_provider: string }
): Promise<{ success: boolean; run_id: string; message: string }> => {
    const response = await apiClient.post(`/funnels/${funnelId}/translate-v2`, data);
    return response.data;
};

// Consultar status de tradução V2 por run_id (estável entre retries, ao
// contrário do task_id da V1).
export const getTranslateV2Status = async (
    runId: string
): Promise<{
    run_id: string;
    funnel_id: number;
    status: 'queued' | 'running' | 'completed' | 'partial' | 'failed' | 'cancelled' | 'skipped' | 'not_found';
    pages_done?: string;
    pages_total?: string;
    pages_skipped?: string;
    current_page?: string;
    error?: string;
}> => {
    const response = await apiClient.get(`/funnels/translate-v2/status/${runId}`);
    return response.data;
};

// Consultar status de tradução V2 por funnel_id (mais prático para polling ao
// entrar na tela, sem precisar guardar run_id localmente). Sem fallback de
// recomputar do banco (diferente da V1) — ver docstring do endpoint no
// ms-meta-ops; se não há run ativo, retorna status "idle".
export const getTranslateV2StatusByFunnel = async (
    funnelId: number
): Promise<{
    run_id?: string;
    funnel_id: number;
    status: 'idle' | 'queued' | 'running' | 'completed' | 'partial' | 'failed' | 'cancelled' | 'skipped';
    pages_done?: string;
    pages_total?: string;
    pages_skipped?: string;
    current_page?: string;
    error?: string;
}> => {
    const response = await apiClient.get(`/funnels/${funnelId}/translate-v2/status`);
    return response.data;
};

// Validar e corrigir divergências de tradução — engine V2. Diferente da V1
// (que dispara 1 task por idioma e retorna vários task_id em
// pages_retranslating), a V2 processa todos os idiomas necessários num único
// run e retorna um único run_id — simplificando o polling no frontend
// (1 run_id em vez de Promise.allSettled sobre vários task_ids).
export const validateTranslationV2 = async (
    funnelId: number,
    pageIds?: string[],
    force?: boolean
): Promise<{ success: boolean; run_id: string; message: string }> => {
    const response = await apiClient.post(`/funnels/${funnelId}/translate-v2/validate`, {
        page_ids: pageIds && pageIds.length > 0 ? pageIds : undefined,
        // `force` só tem efeito no backend quando page_ids está preenchido.
        force: force && pageIds && pageIds.length > 0 ? true : undefined,
    });
    return response.data;
};
